import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, Clock, Globe2, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { API, useApi, errMsg, fmtDate } from "@/lib/api";
import { useApp } from "@/lib/app-context";
import { countryName } from "@/lib/geo";
import { CountrySelect } from "./Misc";

const FREE_FEATURES = ["Purchase invoice storage", "Warranty and guarantee storage", "Multiple coverage periods", "Automatic expiry reminders", "Document viewing and downloading"];
const PLAN_META = {
  free: { storage: "3 Product Records", features: FREE_FEATURES, cta: "Get Started", duration: "Forever" },
  premium: { storage: "20 Product Records", features: ["Includes all Free features", ...FREE_FEATURES.slice(0, 2)], cta: "Choose Premium", duration: "per year" },
  elite: { storage: "50 Product Records", features: ["Includes all Premium features", ...FREE_FEATURES.slice(0, 2)], cta: "Choose Elite", duration: "per year" },
};

const loadRzp = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true);
  s.onerror = () => res(false);
  document.body.appendChild(s);
});

function NotifyDialog({ open, onClose, country, plan }) {
  const { me } = useApp();
  const [email, setEmail] = useState(me?.email || "");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setEmail(me?.email || ""); setConsent(false); } }, [open, me]);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/launch-interest`, { email, country, plan, consent });
      toast.success("Thanks! We'll let you know when paid plans launch in your country.");
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-[24px] p-7" data-testid="notify-dialog">
        <DialogTitle className="font-display text-xl text-ww-navy">Notify Me When Available</DialogTitle>
        <DialogDescription>Premium and Elite are coming soon in {countryName(country)}. We'll email you once when they launch. No payment is taken.</DialogDescription>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-2 h-11 rounded-xl" data-testid="notify-email-input" />
        <label className="mt-2 flex items-start gap-3 text-sm text-ww-muted">
          <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="notify-consent-checkbox" className="mt-0.5" />
          I agree that Warranty Wallet may email me about the paid-plan launch in my country. I can withdraw consent anytime.
        </label>
        <button className="ww-btn-primary mt-4 justify-center" disabled={!consent || !email || busy} onClick={submit} data-testid="notify-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Notify Me"}</button>
      </DialogContent>
    </Dialog>
  );
}

function CheckoutDialog({ quote, onClose, onPaid }) {
  const api = useApi();
  const { me } = useApp();
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!quote) return null;
  const pay = async () => {
    setBusy(true);
    try {
      const order = await api("post", "/billing/order", { plan: quote.plan, billing_country: me.country, acknowledge_terms: ack });
      if (!(await loadRzp())) throw new Error("Could not load the payment window. Check your connection.");
      const rzp = new window.Razorpay({
        key: order.key_id, order_id: order.order_id, amount: order.amount, currency: order.currency,
        name: order.name, description: order.description, prefill: order.prefill, theme: { color: "#087C65" },
        handler: async (resp) => {
          try {
            await api("post", "/billing/verify", resp);
            toast.success(`${quote.plan_name} activated. Thank you!`);
            onPaid();
          } catch (e) { toast.error(errMsg(e)); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (r) => {
        api("post", "/billing/failed", { order_id: order.order_id, reason: r?.error?.description || "" }).catch(() => {});
        toast.error("Payment failed. You have not been charged for a subscription.");
        setBusy(false);
      });
      rzp.open();
      onClose();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-[24px] p-7" data-testid="checkout-dialog">
        <DialogTitle className="font-display text-xl text-ww-navy">Confirm {quote.plan_name}</DialogTitle>
        <DialogDescription>Review your plan before payment.</DialogDescription>
        <dl className="mt-2 space-y-2 rounded-2xl bg-ww-mint/60 p-4 text-sm">
          {[["Plan", `${quote.plan_name} — ${quote.limit} product records`], ["Charge today", quote.amount_display], ["Term", `${fmtDate(quote.start_date)} to ${fmtDate(quote.end_date)}`], ["Type", { new: "New subscription", upgrade: "Upgrade", renewal: "Manual renewal" }[quote.kind]]].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-3"><dt className="text-ww-muted">{k}</dt><dd className="text-right font-semibold text-ww-navy" data-testid={`checkout-${k.toLowerCase().replace(" ", "-")}`}>{v}</dd></div>
          ))}
        </dl>
        <p className="text-xs text-ww-muted">{quote.policy}</p>
        {!quote.payments_configured && <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800" data-testid="checkout-not-configured">Online payments are being set up and are not available yet.</p>}
        <label className="flex items-start gap-3 text-sm text-ww-muted">
          <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="checkout-terms-checkbox" className="mt-0.5" />
          I agree to the Terms and the Refund & Cancellation Policy. This is a one-time annual payment with no automatic renewal.
        </label>
        <button className="ww-btn-primary justify-center" disabled={!ack || busy || !quote.payments_configured} onClick={pay} data-testid="checkout-pay-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${quote.amount_display}`}</button>
      </DialogContent>
    </Dialog>
  );
}

export function PricingCards({ onPaid }) {
  const { isSignedIn } = useAuth();
  const { me, country, setCountry, openSignup, refreshMe } = useApp();
  const api = useApi();
  const cc = me?.country || country;
  const [data, setData] = useState(null);
  const [notify, setNotify] = useState(null);
  const [quote, setQuote] = useState(null);
  useEffect(() => { axios.get(`${API}/pricing`, { params: { country: cc } }).then((r) => setData(r.data)).catch(() => {}); }, [cc]);
  const choose = async (code) => {
    if (code === "free") return isSignedIn ? null : openSignup();
    if (!data?.paid_available) return setNotify(code);
    if (!isSignedIn) return openSignup();
    try { setQuote(await api("post", "/billing/quote", { plan: code, billing_country: me.country })); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const current = me?.plan;
  return (
    <div>
      <div className="mb-8 flex flex-wrap items-center justify-center gap-3 text-sm text-ww-muted">
        <Globe2 className="h-4 w-4 text-ww-emerald" /> Showing plans for
        {me ? <span className="font-semibold text-ww-navy" data-testid="pricing-country-label">{countryName(cc)}</span> :
          <CountrySelect value={cc} onChange={setCountry} testid="pricing-country-select" className="w-52" />}
        {me && <a href="/account" className="text-xs font-semibold text-ww-emerald underline">Change in Account</a>}
      </div>
      <div className="grid gap-6 md:grid-cols-3">
        {["free", "premium", "elite"].map((code) => {
          const p = data?.plans?.find((x) => x.code === code);
          const meta = PLAN_META[code];
          const featured = code === "premium";
          const soon = code !== "free" && data && !data.paid_available;
          const price = code === "free" ? (data?.paid_available ? p?.price : cc === "IN" ? "₹0" : "Free") : p?.price;
          return (
            <div key={code} data-testid={`pricing-card-${code}`} className={`relative flex flex-col rounded-[28px] border p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${featured ? "border-ww-emerald/40 bg-[linear-gradient(170deg,#ffffff,#EAF9F4)] shadow-[0_30px_60px_-30px_rgba(8,124,101,0.45)]" : "border-ww-border bg-white shadow-[0_20px_50px_-35px_rgba(16,43,64,0.35)]"}`}>
              {featured && <span className="absolute -top-3 left-8 rounded-full bg-ww-emerald px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">Most chosen</span>}
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-ww-emerald">{p?.name || code}</p>
              {soon ? (
                <div className="mt-4"><p className="flex items-center gap-2 font-display text-2xl font-semibold text-ww-navy" data-testid={`pricing-${code}-coming-soon`}><Clock className="h-5 w-5 text-ww-emerald" />Coming Soon</p><p className="mt-1 text-sm text-ww-muted">in Your Country</p></div>
              ) : (
                <div className="mt-4 flex items-end gap-1.5"><span className="font-display text-5xl font-semibold tracking-tight text-ww-navy" data-testid={`pricing-${code}-price`}>{price || "—"}</span><span className="pb-1.5 text-sm text-ww-muted">{code === "free" ? "/ Forever" : "/year"}</span></div>
              )}
              <p className="mt-5 rounded-xl bg-ww-cyan px-3 py-2 text-sm font-semibold text-ww-navy">{meta.storage}</p>
              <ul className="mt-6 flex-1 space-y-3">
                {meta.features.map((f) => <li key={f} className="flex items-start gap-2.5 text-sm text-ww-navy/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-ww-emerald" />{f}</li>)}
              </ul>
              {current === code ? (
                <span className="mt-8 rounded-2xl border border-ww-emerald/40 py-3 text-center text-sm font-semibold text-ww-emerald" data-testid={`pricing-${code}-current`}>Current plan</span>
              ) : soon ? (
                <button onClick={() => setNotify(code)} className="ww-btn-ghost mt-8 justify-center" data-testid={`pricing-${code}-notify-button`}>Notify Me When Available</button>
              ) : (
                <button onClick={() => choose(code)} className={`${featured ? "ww-btn-primary" : "ww-btn-ghost"} mt-8 justify-center`} disabled={code === "free" && isSignedIn} data-testid={`pricing-${code}-button`}>{meta.cta}</button>
              )}
            </div>
          );
        })}
      </div>
      <NotifyDialog open={!!notify} plan={notify || "any"} country={cc} onClose={() => setNotify(null)} />
      <CheckoutDialog quote={quote} onClose={() => setQuote(null)} onPaid={() => { refreshMe(); onPaid?.(); }} />
    </div>
  );
}
